import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import T from 'prop-types';
import cx from 'classnames';
import qs from 'qs';

import AppInsightsNavigation from './AppInsightsNavigation';
import Container from '../Container';

import { updatePath } from '../ASOComponents/hooks/usePathFilters';
import {
  hasNoQueryFilters,
  historyReplaceState,
  getCurrentTab,
  getHeaderConfig,
  permissionsMapped,
  getPeriodFilters,
} from './config';
import { getGlobalSettings } from '../../utils/global_settings';

const rootNode = document.getElementById('app-insights-general-root');

const allowedPages = permissionsMapped.reduce((memo, { data_name, tab }) => ({
  ...memo,
  [tab]: rootNode?.dataset?.allowedPages
    ? JSON.parse(rootNode.dataset.allowedPages)[data_name]
    : false,
}), {});

const app = JSON.parse(rootNode?.dataset?.appSummary || '{}');
const appRefNos = JSON.parse(rootNode?.dataset?.appRefs || '{}');

const initialFilters = JSON.parse(rootNode?.dataset?.initialFilters || '{}');
const { periodFilters, defaultPeriod } = initialFilters;

const { permissions } = getGlobalSettings();

const createCustomEvent = (eventName, data) => new CustomEvent(eventName, { detail: data });

const AppSwitcher = ({
  store = app?.store,
  unselectedPageText,
}) => {
  const locationPathname = window.location.pathname;

  const { current: currentTab } = useRef(getCurrentTab(locationPathname));

  const appHeaderConfig = useMemo(() => (
    getHeaderConfig(currentTab, `${locationPathname}${getPeriodFilters()}`, allowedPages)
  ), [currentTab, locationPathname]);

  const isDefault = useCallback((filters) => {
    if (!filters) return true;

    const { period, period_from_date } = filters;
    return period === defaultPeriod && period_from_date === null;
  }, []);

  const queryString = useCallback((filters) => {
    if (isDefault(filters)) return '';

    const { period, period_from_date, period_to_date } = filters;
    const queryFilters = period_from_date ? { period_from_date, period_to_date } : { period };
    return `?${qs.stringify(queryFilters)}`;
  }, []);

  const [state, setState] = useState({
    activeTab: null,
    query: queryString(periodFilters),
  });

  const onActiveTabChanged = (active_tab) => {
    setState(prevState => ({
      ...prevState,
      activeTab: active_tab,
    }));
    document.dispatchEvent(createCustomEvent('filter-will-change', { active_tab }));
  };

  const decorateSubMenuUrl = (name, url, query, has_permission) => {
    const [pathname, ...restSearch] = url.split('?');

    const decoratedUrl = has_permission ? pathname : `${pathname}#prospect`;

    if (['organic_keywords', 'paid_searches'].includes(name)) {
      return updatePath(decoratedUrl, [['country_iso']]);
    }

    const combinedSearch = new URLSearchParams(`?${[restSearch.join(''), query].join('&').replace(/\?/g, '')}`);
    const uniqueParams = new URLSearchParams();

    combinedSearch.entries().forEach(([key, value]) => {
      if (!uniqueParams.has(key)) {
        uniqueParams.set(key, value);
      }
    });

    const uniqueParamsStr = uniqueParams.toString() ? `?${uniqueParams.toString()}` : '';

    return `${decoratedUrl}${uniqueParamsStr}`;
  };

  useEffect(() => {
    const handleInternalFilterChange = (e) => {
      const query = queryString(e.detail);

      setState(prevState => ({ ...prevState, query }));
      historyReplaceState(query);
    };

    document.addEventListener('period-filters-change', handleInternalFilterChange);

    if (periodFilters && !isDefault(periodFilters) && hasNoQueryFilters()) {
      historyReplaceState(queryString(periodFilters));
    }

    return () => {
      document.removeEventListener('period-filters-change', handleInternalFilterChange);
    };
  }, []);

  if (!appHeaderConfig) return null;
  const { description, show_description, sub_menu } = appHeaderConfig;

  const { activeTab, query } = state;

  return (
    <div className={cx('insights-header-box__second-line', { 'with-submenu': !!sub_menu.length })}>
      <Container maxWidth={1128} className="d-flex flex-column align-items-start justify-content-between">
        {!permissions.allowNewGlobalNavigation && (
          <AppInsightsNavigation
            currentTab={currentTab}
            store={store}
            appRefNos={appRefNos}
            allowedPages={allowedPages}
            unselectedStateText={unselectedPageText}
          />
        )}
        {show_description && <div className="insights-header-box__description">{description}</div>}
        {!!sub_menu.length && (
          <div className="insights-header-box__third-line">
            <div className="insights-header-box__nav">
              {sub_menu.map(({
                name, url, is_active, has_permission, value, type,
              }) => (
                <div key={name} className="insights-header-box__nav-item">
                  {type === 'link' && (
                    <a
                      href={decorateSubMenuUrl(value, url, query, has_permission)}
                      className={cx('insights-header-box__nav-item-link', { active: is_active })}
                    >
                      {name}
                    </a>
                  )}
                  {type === 'filter' && (
                    <button
                      type="button"
                      className={cx('insights-header-box__nav-item-link btn btn-link',
                        { active: activeTab ? activeTab === value : is_active })}
                      onClick={() => onActiveTabChanged(value)}
                    >
                      {name}
                    </button>
                  )}
                  {!has_permission && <i className="hicon-lock text-14 text-muted ml-xxs" /> }
                </div>
              ))}
            </div>
          </div>
        )}
      </Container>
    </div>
  );
};

AppSwitcher.propTypes = {
  store: T.string,
  unselectedPageText: T.string,
};

export default AppSwitcher;
